import React, { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { InternalStandardProps, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Layout } from "../Layout";
import { useHistory } from "react-router-dom";
import {
    useGlobalGifts,
    useProspectingUser,
    useReceivedMemberAudioGifts,
    useReceivedProspectAudioGifts,
} from "JS/React/Hooks/Gifts";
import {
    Content,
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
} from "JS/Models";
import { AppHeader } from "JS/React/Components/AppHeader";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useRouting } from "JS/React/Hooks/Routes";
import { useGetFavorites, useGlobalFavorites } from "JS/React/Hooks/Favorites";
import { uniqBy } from "lodash-es";
import { useAudios, useGlobalAudios } from "JS/React/Hooks/Audio";
import { messaging } from "JS/Helpers/UserMessaging";
import { FavoritesTypes } from ".";
import { ListingComponent } from "../Audios/ListingComponent";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
export interface CategoriesProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const FavoritesAudiosListing = (props: any) => {
    const classes = useStyles(props);
    const { className, ...rest } = props;
    const {
        homeStack,
        favoritesAudiosStack,
        setGlobalFavoritesAudiosStack,
        resetFavoritesAudiosStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        favoritesAudiosStack?.searchQuery || "",
    );
    const [headerTitle, setHeaderTitle] = useState("Favorite Audios");
    const { resumeUserBundles, resumeUserAudios } = useGlobalAudios();

    const history = useHistory();

    const { audios } = useGlobalAudios();
    const { loading: audiosLoading } = useAudios();
    const { isProsUser, loading: loadingProsStatus } = useProspectingUser();

    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();

    const { loading: receivedMemberAudioGiftsLoading } =
        useReceivedMemberAudioGifts(!!receivedMemberAudios?.length);

    const { loading: receivedProspectAudioGiftsLoading } =
        useReceivedProspectAudioGifts(
            !isProsUser || !!receivedProspectAudios?.length,
        );

    const { favNids } = useGlobalFavorites();
    const { loading: favLoading } = useGetFavorites(!!favNids?.length);

    const { linkProvider } = useRouting();

    const filteredAudios = useMemo(() => {
        return uniqBy(
            [...audios, ...receivedMemberAudios, ...receivedProspectAudios],
            (x) => x.nid,
        );
    }, [audios, receivedMemberAudios, receivedProspectAudios]);

    const filterFavoritesAudios = useMemo(
        () => filteredAudios.filter((audio) => favNids.includes(audio.nid)),
        [filteredAudios, favNids],
    );

    const searchResults = useMemo(() => {
        return filterFavoritesAudios?.filter(
            (d) =>
                d?.title
                    ?.toLocaleLowerCase()
                    ?.includes(searchQuery?.toLocaleLowerCase().trim()) ||
                d?.description
                    ?.toLocaleLowerCase()
                    ?.includes(searchQuery?.toLocaleLowerCase().trim()),
        );
    }, [filterFavoritesAudios, searchQuery]);

    useEffect(() => {
        setGlobalFavoritesAudiosStack({
            searchQuery: searchQuery,
        });
    }, [searchResults]);

    const isAudiosContentLoaded =
        !audiosLoading &&
        !receivedMemberAudioGiftsLoading &&
        !receivedProspectAudioGiftsLoading &&
        !loadingProsStatus;

    useEffect(() => {
        window.scrollTo(0, 500);
        window.scrollTo(0, 0);
    }, []);
    const isResumeable = (filteredAudio: Content) => {
        if (+filteredAudio.no_of_files > 1) {
            let filterResumeBundle =
                resumeUserBundles &&
                resumeUserBundles.filter((x) => x.nid === filteredAudio.nid);
            return filterResumeBundle.length > 0;
        } else {
            if (
                resumeUserAudios &&
                resumeUserAudios.filter((x) => x.nid === filteredAudio.nid)
                    .length > 0
            ) {
                const resumeData = resumeUserAudios.find(
                    (x) => x.nid === filteredAudio.nid,
                );
                return !!resumeData;
            } else {
                return false;
            }
        }
    };
    const { handleListItemClick } = useHandlers(
        searchQuery,
        FavoritesTypes.FAVAUDIOS,
    );
    return (
        <Layout>
            <div className={clsx(className, classes.root)} {...rest}>
                <AppHeader
                    title={headerTitle || "Audios"}
                    canGoBack
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={"Search Audios by title/description"}
                    onBackClick={() => {
                        setSearchQuery("");
                        resetFavoritesAudiosStack();
                        if (!homeStack?.isHomeTrack) {
                            history.push(
                                linkProvider.react.favorites().index(),
                            );
                        } else {
                            history.push(linkProvider.react.home());
                        }
                    }}
                />
                {isAudiosContentLoaded && !favLoading ? (
                    <ListingComponent
                        listContent={searchResults as Content[]}
                        searchQuery={searchQuery}
                        categoryId={FavoritesTypes.FAVAUDIOS}
                        showSortHeader={true}
                        setGlobalStack={setGlobalFavoritesAudiosStack}
                        globalStack={favoritesAudiosStack}
                        isResumeable={isResumeable}
                        favoritesContent={favNids}
                        handleListItemClick={handleListItemClick}
                        controlType="audioFavorite"
                    />
                ) : (
                    <AppBackdropProgress
                        open={true}
                        backdropText={
                            favLoading
                                ? messaging.loader.favorites
                                : messaging.loader.audios
                        }
                    />
                )}
            </div>
        </Layout>
    );
};
const useHandlers = (searchQuery: string, categoryId: string) => {
    const { logFirebaseEvent } = useFirebaseLogger();
    const { linkProvider } = useRouting();
    const history = useHistory();

    const handleListItemClick = (
        filteredAudio: Content,
        isAutoPlay: string,
        scrollIndex: number,
    ) => {
        if (searchQuery) {
            const action: FirebaseEventAction = {
                action: EventActions.SEARCH,
                category: filteredAudio.isReceived
                    ? EventCategories.GIFT_AUDIOS
                    : EventCategories.AUDIOS,
                nId: filteredAudio.nid,
                skuId: filteredAudio.sku_id,
                contentTitle: filteredAudio.title,
                searchQuery: searchQuery,
            };

            logFirebaseEvent(
                filteredAudio.isReceived
                    ? EventNames.GIFT_AUDIO_SEARCH_OPENED
                    : EventNames.AUDIO_SEARCH_AUDIO_OPENED,
                action,
            );
        }
        history.push(
            linkProvider.react
                .favorites()
                .favoriteAudiosDetail(
                    filteredAudio.nid,
                    categoryId,
                    "true",
                    isAutoPlay,
                    {
                        scrollTo: scrollIndex ? +scrollIndex : 0,
                    },
                ),
        );
    };

    return { handleListItemClick };
};
const useStyles = makeStyles((theme: Theme) => ({
    root: {},
}));
